import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import {
  AppHeader,
  Box,
  Button,
  Checkbox,
  CheckboxTypography,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { useHistory } from 'react-router-dom';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { numberHelper } from '../utils/numberHelper';
import { IPaperlessOptOut, Pages, PaperlessOptOutType } from '@lib/types';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { getFormattedDiscountedPrice } from '../utils/formatters';
import { CartProductCode, CurrentPayPlan, FlowType, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { ProductType } from '../application/models/NewJoinSessionData';
import { RemoveWarningModal, RemoveWarningModalContentProps } from '../components/RemoveWarningModal';
import { Icon } from '@ace-clublabs/shared-component-library';
import {
  isAutopayRequired,
  isAutopayAllowed,
  isFullDigitalRequired,
  isPaperlessOptOutAllowed,
  getFullDigitalDisclaimer,
  historyPushState,
  getProgressProps,
} from '../utils/helpers';
import { TotalCostHeader } from '../components/CustomizeMembershipComponents/TotalCostHeader';
import { LineItem } from '../components/LineItem';
import { UtagType } from '../utils/UtagHelper';
import { stringHelper } from '../utils/stringHelper';
import { BenefitsPage } from './BenefitsPage';
import { getCartProductName } from '../utils/helpers';
import { AppProgressHeader } from '../components/AppProgressHeader';
import { ContactSupportCard } from '../components/ContactSupportCard';

export const ReviewPage: React.FC = () => {
  const {
    membershipInfo,
    sessionData,
    refreshSession,
    loadingInfo,
    sendTag,
    updateProducts,
    updateAssociates,
    updateBillPlan,
    isPriceCartLoading,
    getPaymentManifestId,
    updatePaperlessOption,
    updateMembershipForm,
  } = useContext(MembershipAppStateContext);
  const history = useHistory();
  const [deleteInfo, setDeleteInfo] = useState<RemoveWarningModalContentProps | null>(null);
  const [isTagging, setIsTagging] = useState(true);
  const [isChangingMembershipLevel, setIsChangingMembershipLevel] = useState(false);
  const isGiftFlow = sessionData.flowType === FlowType.GIFT;
  const giftTitle = isGiftFlow ? "Let's review your gift membership" : "Let's review!";
  const currentUrl = window.location.href;
  const urlSearchParams = new URL(currentUrl).searchParams;
  const displayLeadGeneration = sessionData.formData.mobileConsent && membershipInfo?.insuranceLeadGeneration?.enabled;

  const isChangingMembershipLevelParam = urlSearchParams.get('isChangingMembershipLevel') === 'true';

  const userAgent = navigator.userAgent || '';
  const isAndroid = /android/i.test(userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;

  let appStoreLink;
  if (isAndroid) {
    appStoreLink =
      'https://play.google.com/store/apps/details?id=com.ace.shell.production&referrer=utm_source%3DACE%26utm_campaign%3DAuto_Club_App_Landing';
  } else if (isIOS) {
    appStoreLink = 'https://apps.apple.com/app/apple-store/id1094393125?pt=205244';
  } else {
    appStoreLink = 'https://www.ace.aaa.com/automotive/driver-resources/auto-club-app.html';
  }

  useEffect(() => {
    setIsChangingMembershipLevel(isChangingMembershipLevelParam);
  }, [isChangingMembershipLevelParam]);

  const showBenefitPage = () => {
    urlSearchParams.set('isChangingMembershipLevel', 'true');
    const newUrl = currentUrl.split('?')[0] + '?' + urlSearchParams.toString();
    historyPushState({ path: newUrl }, '', newUrl);
    setIsChangingMembershipLevel(true);
  };

  const closeBenefitsPage = () => {
    window.history.back();
  };

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }
    const tagPage = sessionData.flowType === FlowType.GIFT ? 'membership:gift:review' : 'review';
    sendTag(UtagType.view, tagPage);
    setIsTagging(false);
  }, [membershipInfo]);

  const saveGenerateAutoLead = (generateAutoLead: boolean) => {
    const updatedFormData = {
      ...sessionData.formData,
      generateAutoLead,
    };
    updateMembershipForm(updatedFormData);
  };

  const flowType = sessionData.flowType;

  const selectedMembershipLevel = sessionData.formData.membershipLevel as MembershipLevel;

  const autoPayDiscountAmount =
    //@ts-ignore
    membershipInfo?.membershipCost?.[selectedMembershipLevel]?.annual?.autoPay?.discountAmount ?? 0;

  const showPaperlessOptOut = isPaperlessOptOutAllowed(membershipInfo?.paperlessOptOuts);
  const showAutopayEnrollment =
    !isAutopayRequired(membershipInfo!, sessionData.formData) && isAutopayAllowed(membershipInfo!);
  const showPaperlessOrAutopaySection = showAutopayEnrollment || showPaperlessOptOut;

  const showFullDigitalDisclaimer = isFullDigitalRequired(membershipInfo?.paperlessOptOuts);

  const ersFee = membershipInfo?.membershipCost?.fees?.sameDayServiceFee;

  const salesTax = sessionData?.priceCart?.salesTax || 0;

  const CartProductCodeMap = {
    [ProductType.RV]: CartProductCode.RV,
    [ProductType.ERS]: CartProductCode.ERSFEE,
  };

  const mapWarningTitleById = {
    [ProductType.RV]: 'Are you sure you want to remove roadside for RV/motorcycle?',
    [ProductType.ERS]: 'Are you sure you want to remove same day roadside service?',
  };

  const mapWarningContentById: Partial<Record<ProductType, string>> = {
    [ProductType.ERS]: `If you have a vehicle disablement before you join, that disablement will not be covered after you join as one of your four allowable service calls per membership year. If you would like such service, you may request service for an additional $${ersFee} non-refundable service fee.`,
  };

  const isMonthly = sessionData.formData.billPlan === CurrentPayPlan.RECURRINGMONTHLY;

  const physicalCard: IPaperlessOptOut | undefined = membershipInfo?.paperlessOptOuts?.find(
    (p) => p.type === 'PHYSICAL_CARD'
  );
  const paperless: IPaperlessOptOut | undefined = membershipInfo?.paperlessOptOuts?.find(
    (p) => p.type === 'PAPERLESS_BILLING'
  );
  const magazine: IPaperlessOptOut | undefined = membershipInfo?.paperlessOptOuts?.find((p) => p.type === 'MAGAZINE');

  const promoCodeSection =
    flowType === FlowType.NEW_JOIN ? (
      <>
        <Typography variant="b2Regular" sx={{ paddingTop: '17px' }}>
          Promo code
        </Typography>

        <Box className="promo-input-container">
          <TextField id="promoInput" placeholder="AAA2023" fullWidth />
          <Button id="applyButton" label="Apply" onClick={() => {}} className="apply-button buttonText" />
        </Box>
      </>
    ) : (
      <></>
    );

  const getClassicBaseCost = (): string => {
    const defaultCost = 0.0;
    return numberHelper.formatMoney(membershipInfo?.membershipBaseCost?.CLASSIC?.annual?.baseCost ?? defaultCost);
  };

  const handleCta = async (updatedBillPlan: CurrentPayPlan): Promise<void> => {
    const shouldUpdateBillPlan = !isGiftFlow && updatedBillPlan !== sessionData.formData.billPlan;
    if (shouldUpdateBillPlan) {
      await updateBillPlan(updatedBillPlan);
    }
    getPaymentManifestId();
  };

  const productType = getCartProductName(CartProductCode.SERVICELEVEL);

  const getTotalCost = (): string => {
    const defaultCost = 0.0;
    return numberHelper.formatMoney(sessionData?.priceCart?.total ?? defaultCost);
  };

  const isMajorRocket = flowType === FlowType.MAJOR_ROCKET;

  const serviceLevelCost = sessionData?.priceCart?.items?.find((item) => item.code === CartProductCode.SERVICELEVEL);
  const admissionFeeCost = sessionData?.priceCart?.items?.find((item) => item.code === CartProductCode.ADMISSION);
  const familyUpgradeCost = sessionData?.priceCart?.items?.find((item) => item.code === CartProductCode.FAMILYCOST);
  const autopayDiscount = sessionData?.priceCart?.items?.find((item) => item.code === CartProductCode.AUTOPAY_DISCOUNT);

  const formattedServiceLevelDiscount = Number.isInteger(serviceLevelCost?.discount)
    ? `$${serviceLevelCost?.discount?.toString() || '0'}`
    : stringHelper.formatMoney(serviceLevelCost?.discount?.toString() || '0');

  const majorRocketServiceLevel = (
    <>
      <Box className="review-item-container">
        <Typography
          data-di-mask={`false`}
          className={`${productType}-description`}
          variant="b2Regular"
          sx={{ padding: '16px 0' }}
        >
          Classic member
        </Typography>
        <Box className="flex-row-container">
          <>
            <Typography
              variant="b2Regular"
              className={`${productType}-full-price`}
              sx={{ color: '#9E9E9E', textDecorationLine: 'line-through', marginRight: '8px' }}
            >
              {getClassicBaseCost()}
            </Typography>

            <Typography
              variant="b2Regular"
              className={`${productType}-discounted-price`}
              sx={{ color: '#7527B9', marginRight: '44px' }}
            >
              (Coupon redeemed)
            </Typography>
          </>
        </Box>
      </Box>
    </>
  );

  const progressProps = getProgressProps(sessionData.flowType!, Pages.ReviewPage);

  const serviceLevelSection =
    flowType === FlowType.MAJOR_ROCKET ? (
      <>{majorRocketServiceLevel}</>
    ) : (
      <>
        <LineItem
          {...serviceLevelCost!}
          mask
          description={`${serviceLevelCost?.description} - ${sessionData.formData.firstName} ${sessionData.formData.lastName}`}
        />
        {flowType === FlowType.T_MOBILE && (
          <Typography
            className="tmobile-year-on-us-message"
            variant="b3Regular"
            sx={{ color: '#7527B9', marginTop: '-0.5rem', marginBottom: '1rem' }}
          >
            {`Year on Us by T-Mobile (${formattedServiceLevelDiscount} value)`}
          </Typography>
        )}
        {flowType === FlowType.EMPLOYEE && (
          <Typography
            className="employee-discount-message"
            variant="b3Regular"
            sx={{ color: '#7527B9', marginTop: '-0.5rem', marginBottom: '1rem', display: 'block' }}
          >
            {`Employee discount (${formattedServiceLevelDiscount} value)`}
          </Typography>
        )}
        {flowType === FlowType.GROUPON && (
          <Typography
            className="groupon-redeemed-message"
            variant="b3Regular"
            sx={{ color: '#7527B9', marginTop: '-0.5rem', marginBottom: '1rem' }}
          >
            {`Groupon offer applied`}
          </Typography>
        )}
      </>
    );

  const admissionFeeSection =
    flowType === FlowType.MAJOR_ROCKET ? (
      <>
        <hr className="overview-line"></hr>
        <Box className="review-item-container">
          <Typography variant="b2Regular" sx={{ padding: '16px 0' }}>
            New member admission fee
          </Typography>
          <Box className="flex-row-container">
            <Box className="flex-row-container">
              <Typography
                variant="b2Regular"
                className={`${productType}-cost`}
                sx={{ color: '#7527B9', marginRight: '44px' }}
              >
                Waived
              </Typography>
            </Box>
          </Box>
        </Box>
      </>
    ) : (
      <LineItem {...admissionFeeCost!} />
    );

  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </Container>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return isChangingMembershipLevel ? (
    <BenefitsPage onContinue={closeBenefitsPage} isUpgrading={true} />
  ) : (
    <>
      <AppProgressHeader {...progressProps} />

      <Typography variant="h5" className="review-header">
        {giftTitle}
      </Typography>
      <TotalCostHeader formattedCost={getTotalCost()} />
      {/* {promoCodeSection} */}
      <Box className="overview-container">
        <Box className="overview-title">Membership overview</Box>
        <hr className="overview-line"></hr>
        {/* Service Level Cost */}
        {serviceLevelSection}

        {/* Family Upgrade cost line item*/}
        {familyUpgradeCost ? <LineItem {...familyUpgradeCost!} /> : <></>}

        {/* Associate line items */}
        {sessionData.formData.associates?.map((associate) => {
          const associatePriceCart = sessionData.priceCart?.items?.find((a) => a.id === associate.virtualId);
          return (
            <LineItem
              {...associatePriceCart!}
              mask
              onDelete={() =>
                setDeleteInfo({
                  title: `Are you sure you want to remove ${associate.firstName} ${associate.lastName} from your membership?`,
                  confirmRemove: () => {
                    updateAssociates('remove', associate);
                  },
                })
              }
            />
          );
        })}

        {/* Product line items */}
        {sessionData.formData.products?.map((product) => {
          const productPriceCart = sessionData.priceCart?.items?.find((a) => a.code === CartProductCodeMap[product]);
          return (
            <LineItem
              {...productPriceCart!}
              onDelete={() =>
                setDeleteInfo({
                  title: mapWarningTitleById[product] ?? undefined,
                  warningContent: mapWarningContentById?.[product] ?? undefined,
                  confirmRemove: () => {
                    updateProducts('remove', ProductType[product]);
                  },
                })
              }
            />
          );
        })}

        {/* Admission cost line item*/}
        {admissionFeeSection}

        {/* Auto Pay line item */}
        {autopayDiscount ? (
          <>
            <LineItem
              id="autopay"
              code={CartProductCode.AUTOPAY_DISCOUNT}
              price={0}
              discount={autoPayDiscountAmount}
              discountedPrice={-autoPayDiscountAmount}
              description="One-time Auto Pay discount"
              color="#7527B9"
              icon="tag-solid"
            />
          </>
        ) : (
          <></>
        )}

        {/* Sales Tax line item */}
        {salesTax > 0 ? (
          <LineItem id="sales-tax" code={CartProductCode.SALESTAX} price={salesTax} description="Sales tax" />
        ) : (
          <></>
        )}

        {salesTax || autopayDiscount ? <hr className="overview-line"></hr> : <></>}

        <Box className="total-container">
          <Typography
            variant="b1Bold"
            sx={{ color: '#1C4DA6', marginLeft: '16px', fontWeight: 700 }}
            className="total-title"
          >
            Total<sup>*</sup>
          </Typography>
          {isPriceCartLoading ? (
            <CircularProgress variant="indeterminate" size={25} className="total-cost-spinner" />
          ) : (
            <Typography
              variant="b1Bold"
              className="total-price"
              sx={{ color: '#1C4DA6', marginRight: '58px', fontWeight: 700 }}
            >
              {getTotalCost()}
            </Typography>
          )}
        </Box>

        {displayLeadGeneration && (
          <Box
            sx={{
              backgroundColor: '#FAF8F4',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1rem',
              '& .MuiFormControlLabel-root': { width: '100%', padding: '0 !important' },
            }}
          >
            <Checkbox
              variant="text"
              name="generateAutoLead"
              className="generateAutoLead"
              id="generateLead-checkbox"
              label={
                <Typography variant="b2Regular">
                  Your AAA membership unlocks exclusive auto insurance savings — get your quote now.
                </Typography>
              }
              fullWidth
              checked={sessionData.formData.generateAutoLead ?? false}
              onClick={(mouseEvent) => {
                const target = mouseEvent.target as HTMLInputElement;
                saveGenerateAutoLead(target.checked);
              }}
            />
          </Box>
        )}

        <Stack sx={{ gap: '1rem' }}>
          {!isGiftFlow && (isAutopayRequired(membershipInfo!, sessionData.formData) || showAutopayEnrollment) ? (
            <>
              <Paper
                elevation={8}
                sx={{
                  //backgroundColor: '#FAF8F4',
                  borderRadius: '10px',
                  padding: '0.75rem',
                  // marginTop: '1rem',
                  marginBottom: '1rem',
                }}
              >
                <Stack className="review-terms-section">
                  <Typography variant="b3Regular" sx={{ color: '#404040' }}>
                    <Icon
                      iconName="calendar-clock-duotone"
                      sx={{ fontSize: 'x-large', color: '#088BA6', height: '22px', width: '20px' }}
                    />
                    <Typography
                      className="card-title review-item-container"
                      variant="b1Bold"
                      sx={{ fontWeight: '700', marginBottom: '.25rem', fontSize: '0.875rem' }}
                    >
                      {showAutopayEnrollment ? (
                        <>
                          Set up Auto Pay
                          <Typography
                            className="flex-row-container"
                            sx={{
                              backgroundColor: '#F3E9FE',
                              borderRadius: '4px',
                              color: '#7527B9',
                              fontSize: '14px',
                              fontWeight: '500',
                              paddingLeft: '0.5rem',
                              paddingRight: '0.5rem',
                            }}
                          >
                            Save {getFormattedDiscountedPrice(autoPayDiscountAmount)}
                          </Typography>
                        </>
                      ) : (
                        <>Auto Pay required to join AAA</>
                      )}
                    </Typography>
                    By clicking below, you agree to enroll in Auto Pay per the{' '}
                    <Link
                      className="benefit-link terms-condition-link"
                      href={`https://app.ace.aaa.com/payments/termsandconditions?variant=mmp&clubcode=${membershipInfo?.clubCode}`}
                      target="_blank"
                      variant="b3Regular"
                    >
                      Terms
                    </Link>
                    . Your membership will renew automatically each year for {!isMonthly ? <>1 year</> : <></>}
                    {isMonthly ? (
                      <>a 1-year term for annual dues option, or each month for monthly dues option,</>
                    ) : (
                      <></>
                    )}{' '}
                    at the amount specified using the payment method you enter next, unless you cancel Auto Pay prior to
                    your membership expiration date. To cancel Auto Pay, go to{' '}
                    <Link
                      className="benefit-link terms-condition-link"
                      href={`https://account.app.ace.aaa.com`}
                      target="_blank"
                      variant="b3Regular"
                    >
                      My Account
                    </Link>
                    , visit your{' '}
                    <Link
                      className="benefit-link terms-condition-link"
                      href={`https://www.ace.aaa.com/locations`}
                      target="_blank"
                      variant="b3Regular"
                    >
                      AAA branch
                    </Link>{' '}
                    or call{' '}
                    <Link
                      className="benefit-link terms-condition-link"
                      href={`tel:1-877-428-2277`}
                      target="_blank"
                      variant="b3Regular"
                    >
                      1-877-428-2277
                    </Link>
                    .
                  </Typography>
                  <Typography variant="b3Regular" sx={{ color: '#404040', marginTop: '5px' }}>
                    *These recurring dues may change at renewal with notice (e.g., promotional rates/discounts expire).
                  </Typography>
                </Stack>
                <Box className="enrollments-signup" id="checkbox-align">
                  {showPaperlessOrAutopaySection ? (
                    <>
                      {showPaperlessOptOut ? (
                        <>
                          <hr className="overview-line" />
                          <Stack className="paperless-options-content" sx={{ marginTop: '0.5rem' }}>
                            <Typography variant="h6Medium">Paperless options</Typography>
                            <Typography variant="b3Regular">
                              Opt out of receiving paper documents in the mail and access your AAA information
                              digitally. You can change your settings anytime in My Account. See{' '}
                              <Link
                                className="benefit-link paperless-terms-condition-link"
                                href={`https://www.ace.aaa.com/legal/paperless-terms-and-conditions.html`}
                                target="_blank"
                                variant="b3Regular"
                                sx={{ color: '#0156A3' }}
                              >
                                Terms and Conditions
                              </Link>
                              .
                            </Typography>
                          </Stack>
                          <Stack className="opt-out-checkboxes">
                            <Typography variant="b1Medium" sx={{ margin: '1rem 1rem 0 0' }}>
                              I don't want to receive
                            </Typography>
                            <Stack padding={0}>
                              {paperless?.allowed ? (
                                <Checkbox
                                  variant="text"
                                  sx={{
                                    fontSize: '1rem',
                                  }}
                                  name="paperless"
                                  value="paperless"
                                  id="paperless-billing-or-documents-checkbox"
                                  label="Paper billing or documents"
                                  checked={sessionData.formData.paperlessBilling}
                                  inputProps={{
                                    onClick: () => {
                                      updatePaperlessOption(
                                        PaperlessOptOutType.PAPERLESS_BILLING,
                                        !sessionData.formData.paperlessBilling
                                      );
                                    },
                                  }}
                                ></Checkbox>
                              ) : (
                                <></>
                              )}
                              {physicalCard?.allowed ? (
                                <Checkbox
                                  variant="text"
                                  name="physicalCard"
                                  value="physicalCard"
                                  id="physical-membership-card-checkbox"
                                  sx={{ marginTop: '0', marginBottom: '0' }}
                                  checked={sessionData.formData.declinePhysicalCard}
                                  inputProps={{
                                    onClick: () => {
                                      updatePaperlessOption(
                                        PaperlessOptOutType.PHYSICAL_CARD,
                                        !sessionData.formData.declinePhysicalCard
                                      );
                                    },
                                  }}
                                  label={
                                    <>
                                      <Typography sx={{ fontSize: '16px !important' }}>
                                        A physical membership card
                                      </Typography>
                                      <Typography
                                        sx={{ fontSize: '14px !important', color: '#707070', whiteSpace: 'nowrap' }}
                                      >
                                        Get your digital card on the Auto Club App
                                      </Typography>
                                    </>
                                  }
                                ></Checkbox>
                              ) : (
                                <></>
                              )}
                              {magazine?.allowed ? (
                                <Checkbox
                                  variant="text"
                                  name="magazine"
                                  value="magazine"
                                  id="magazine-checkbox"
                                  label={magazine.label}
                                  sx={{ marginTop: '0' }}
                                  checked={sessionData.formData.declineMagazine}
                                  inputProps={{
                                    onClick: () => {
                                      updatePaperlessOption(
                                        PaperlessOptOutType.MAGAZINE,
                                        !sessionData.formData.declineMagazine
                                      );
                                    },
                                  }}
                                ></Checkbox>
                              ) : (
                                <></>
                              )}
                            </Stack>
                          </Stack>
                        </>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </Box>
              </Paper>
            </>
          ) : (
            <></>
          )}
        </Stack>

        <Box className="review-page-button">
          <Button
            label={
              <>
                {isPriceCartLoading ? (
                  <Box className="button-spinner">
                    <CircularProgress variant="indeterminate" size={20} color="inherit" />{' '}
                  </Box>
                ) : (
                  <></>
                )}
                Agree and continue
              </>
            }
            disabled={isPriceCartLoading}
            fullWidth
            onClick={async () => {
              handleCta(isMonthly ? CurrentPayPlan.RECURRINGMONTHLY : CurrentPayPlan.RECURRINGCREDITORDEBITCARD);
            }}
            variant="contained"
            size="medium"
            id="nextButton"
          ></Button>
        </Box>
        {showAutopayEnrollment ? (
          <>
            <Box className="skip-and-pay-button" style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              <Button
                style={{ backgroundColor: 'white', color: '#016BCD' }}
                label={
                  <>
                    {isPriceCartLoading ? (
                      <Box className="button-spinner">
                        <CircularProgress variant="indeterminate" size={20} color="inherit" />{' '}
                      </Box>
                    ) : (
                      <></>
                    )}
                    Skip and pay
                  </>
                }
                disabled={isPriceCartLoading}
                fullWidth
                onClick={async () => {
                  handleCta(CurrentPayPlan.ONETIME);
                }}
                variant="contained"
                size="medium"
                id="skipNextButton"
              ></Button>
            </Box>
          </>
        ) : (
          <></>
        )}
        {showFullDigitalDisclaimer ? (
          <>
            <Box
              sx={{
                backgroundColor: '#FAF8F4',
                borderRadius: '10px',
                padding: '0.75rem',
                marginTop: '1rem',
                marginBottom: '1rem',
              }}
            >
              <Stack className="full-digital-section">
                <Typography variant="b3Regular" sx={{ color: '#404040' }}>
                  {getFullDigitalDisclaimer(membershipInfo?.clubCode ?? '')} See{' '}
                  <Link
                    className="benefit-link"
                    href="https://www.ace.aaa.com/legal/paperless-terms-and-conditions.html"
                    target="_blank"
                    variant="b3Regular"
                  >
                    Terms and Conditions
                  </Link>
                  . You can update your settings anytime in{' '}
                  <Link
                    className="benefit-link terms-condition-link"
                    href={`https://account.app.ace.aaa.com`}
                    target="_blank"
                    variant="b3Regular"
                  >
                    My Account
                  </Link>
                  .
                </Typography>
              </Stack>
            </Box>
          </>
        ) : (
          <></>
        )}
        <ContactSupportCard />
      </Box>
      <RemoveWarningModal
        title={deleteInfo?.title ?? ''}
        warningContent={deleteInfo?.warningContent ?? ''}
        showModal={!!deleteInfo}
        closeModal={() => setDeleteInfo(null)}
        confirmRemove={() => {
          deleteInfo?.confirmRemove();
        }}
      />
    </>
  );
};
